import { Component, type ErrorInfo, type ReactNode } from 'react'
import { createDebug } from '../../utils/debugLog'

// Banners are non-critical, app-wide UI. If BannerHost ever throws during
// render, it must NOT take down the whole app (it mounts at the root of <App/>,
// outside the router, and there is no other boundary above it). This boundary
// renders nothing on error — the app keeps working, just without banners — and
// forwards the crash to nad-debug.log so it's still visible.
const errLog = createDebug('dw:dynamic-banner:boundary')

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

export class BannerBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    errLog({
      event: 'banner-render-crash',
      feature: 'dynamic-banner',
      error: error.message,
      // Normalize to null so the metadata stays JSON-primitive-only (an
      // `undefined` field would be dropped during serialization).
      stack: error.stack ?? null,
      componentStack: errorInfo.componentStack ?? null,
    })
  }

  render() {
    if (this.state.hasError) return null
    return this.props.children
  }
}
